import type { ComponentType } from 'react'
import { Outlet } from 'react-router-dom'
import { CampaignsPage } from './pages/app/campaigns/campaigns-page'
import { DealsPage } from './pages/app/deals/deals'
import { DealDetailPage } from './pages/app/deals/DealDetailPage'
import K12DistrictPage from './pages/app/k12-district-page'
import { ProductsPage } from './pages/app/products'
import { BrandsPage } from './pages/app/brands'
import { AppLayout } from './pages/app/layout'
import { OpportunitiesPage } from './pages/app/opportunities/opportunities'
import { AuthLayout } from './pages/auth/layout'
import Login from './pages/auth/login'
import Register from './pages/auth/register'
import ResetPassword from './pages/auth/reset-password'
import ResetPasswordConfirm from './pages/auth/reset-password-confirm'
import SSOLogin from './pages/auth/sso-login'
import SSOCallback from './pages/auth/sso-callback'
import Verified from './pages/auth/verified'
import { NotFoundContent } from './pages/app/not-found-content'
import { sentryCreateBrowserRouter } from 'services/sentry'
import { usePostHogPageview } from 'hooks/usePostHogPageview'
import { Rebates } from 'pages/app/rebates'
import { RebateOfferDetails } from './pages/app/rebates/rebate-offer-details'
import Oops from 'pages/oops'
import NotAuthorized from 'pages/not-authorized'
import LodgingPage from 'pages/app/google_places/lodging'
import LodgingDoorPage from 'pages/app/google_places/lodgingDoor'
import OptOutForm from 'pages/common/opt-out-form'
import DataRequestForm from 'pages/common/data-request-form'
import DataRequestSuccess from 'pages/common/data-request-success'
import OptOutSuccess from 'pages/common/opt-out-success'
import SubscriptionAgreement from 'pages/common/subscription-agreement'
import ConveniencePage from 'pages/app/google_places/convenience'
import ConvenienceDoorPage from 'pages/app/google_places/convenienceDoor'
import { HomePage } from './pages/app/home'
import {
  ImportPreviewsPage as ImportPreviewsPageV2,
  ImportPreviewPage as ImportPreviewPageV2,
} from 'pages/app/importer-page-v2'
import { SettingsLayout } from 'pages/app/settings/layout'
import { CompanySettingsPage } from 'pages/app/settings/company'
import { UsersSettingsPage } from 'pages/app/settings/users'
import { PlatformSettingsPage } from 'pages/app/settings/platform'
import { DataSettingsPage } from 'pages/app/settings/data'
import { NotificationsSettingsPage } from 'pages/app/settings/notifications'
import { SettingsIndexRedirect } from 'pages/app/settings/SettingsIndexRedirect'
import { MiscSettingsPage } from 'pages/app/settings/misc'
import { AutomationsSettingsPage } from 'pages/app/settings/automations'
import { AISettingsPage } from 'pages/app/settings/ai'
import { DoorProxyPage } from 'pages/app/door-proxy-page'
import { useAuth } from 'context/authentication/useAuth'
import ProtectedRoute from 'utils/ProtectedRoute'
import RedirectRestaurants from 'utils/RedirectRestaurants'
import { PageWithTitle } from 'utils/PageWithTitle'
import { Dashboard } from 'pages/app/dashboard'
import { ContactsPage } from 'pages/app/contacts/contacts'
import ContactImportPage from 'features/contact-import/ContactImportPage'
import { ActivityPageV2 } from 'pages/app/activity/activityV2'
import { CompanyPage } from 'pages/app/company'
import Restaurant from 'pages/app/restaurant'
import { ChainPage } from 'pages/app/chain-page'
import University from 'pages/app/university'
import HospitalPage from 'pages/app/hospital'
import { Navigate, RouterProvider, useParams } from 'react-router-dom'
import NurseHomePage from 'pages/app/nursinghome'
import FeatureProtectedRoute from 'utils/FeatureProtectedRoute'
import { SamplesDashboard } from 'pages/app/samples/SamplesDashboard'
import { CampaignRouterPage } from 'pages/app/campaigns/campaign-router-page'
import { CampaignRouteLayout } from 'pages/app/campaigns/campaign-route-layout'
import { CampaignDashboardPage } from 'pages/app/campaigns/campaign-dashboard-page'
import { CampaignDetailPage } from 'pages/app/campaigns/campaign-detail-page'
import { SequenceWizardPage } from 'pages/app/campaigns/sequence-wizard-page'
import Failed from 'pages/auth/failed'
import { CommonLayout } from 'pages/common/layout'
import Terms from 'pages/common/terms'
import Privacy from 'pages/common/privacy'
import RebatePlatformAgreement from 'pages/common/rebates-platform-agreement'
import { DocLibraryPage } from 'pages/DocLibrary'
import { ComponentLibraryPage } from 'component-catalog/page'
import ReportingLibraryPage from 'features/reporting-prototype'
import ReportDetailPage from 'features/reporting-prototype/ReportDetailPage'
import ReportingDashboardPage from 'features/reporting-prototype/DashboardPage'
import ReportBuilderPage from 'features/reporting-prototype/BuilderPage'
import DashboardBuilderPage from 'features/reporting-prototype/DashboardBuilderPage'
import { ReportingPrototypeGate } from 'features/reporting-prototype/ReportingPrototypeGate'
import { PATHS } from 'paths'
import { RemindersPage } from 'pages/app/reminders/web'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { EmailIntegrationSettingsPage } from 'pages/app/settings/email-integration'
import { OutreachSettingsPage } from 'pages/app/settings/outreach'

// Root layout that wraps all routes to track pageviews
function RootLayout() {
  usePostHogPageview()
  return <Outlet />
}

// Designer-mode playground: auto-registers any *.tsx file under pages/__playground__/
// as a route at /__playground/<filename>. The directory is gitignored — these routes
// only exist in local dev sessions and never ship. See .claude/skills/designer-mode/SKILL.md.
const playgroundModules = import.meta.glob<{
  default?: ComponentType
}>('./pages/__playground__/*.tsx', { eager: true })
const playgroundRoutes = Object.entries(playgroundModules).flatMap(
  ([path, mod]) => {
    const Component = mod.default
    if (!Component) {
      // Playground file is missing a default export — skip it gracefully so a
      // half-finished file doesn't crash the whole dev session at module load.
      console.warn(`[playground] ${path} has no default export — route skipped`)
      return []
    }
    const slug = path.match(/__playground__\/(.+)\.tsx$/)?.[1] ?? 'unknown'
    return [
      {
        path: `__playground/${slug}`,
        element: (
          <ProtectedRoute>
            <Component />
          </ProtectedRoute>
        ),
      },
    ]
  }
)

/**
 * Redirects legacy /opportunities/:category/:sub_category/ URLs to /targets/:category/:sub_category/.
 * Preserves URL parameters during redirect for backwards compatibility (FIR-5979).
 */
function RedirectOpportunitiesToTargets() {
  const { category, sub_category } = useParams()
  return <Navigate to={`/targets/${category}/${sub_category}/`} replace />
}

function ReportDetailPageWithKey() {
  const { viewId } = useParams()
  return <ReportDetailPage key={viewId} />
}

function DefaultLandingRoute() {
  const { isLimited } = useAuth()
  const featureFlags = useFeatureFlagService()

  if (featureFlags.isLoading) {
    return null
  }

  if (isLimited) {
    return <Navigate to="/samples" replace />
  }

  if (featureFlags.enableHomepage) {
    return <Navigate to="/home" replace />
  }

  return <RedirectRestaurants />
}

export default function Desktop() {
  const appRouter = sentryCreateBrowserRouter(
    [
      {
        // Root wrapper for PostHog pageview tracking
        element: <RootLayout />,
        children: [
          {
            path: '/',
            element: <AppLayout />,
            errorElement: <Oops />,
            children: [
              {
                path: '',
                element: (
                  <ProtectedRoute minimumRole="Limited">
                    <DefaultLandingRoute />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'home',
                element: (
                  <ProtectedRoute>
                    <FeatureProtectedRoute featureFlag="enableHomepage">
                      <PageWithTitle title="Home">
                        <HomePage />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'dashboard',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Dashboard">
                      <Dashboard />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'contacts/:type?/',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Contacts">
                      <ContactsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'contacts/import',
                element: (
                  <ProtectedRoute>
                    <FeatureProtectedRoute featureFlag="enableContactImportWizard">
                      <PageWithTitle title="Import Contacts">
                        <ContactImportPage />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'activity/:type?/',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Activity">
                      <ActivityPageV2 />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reminders',
                element: (
                  <ProtectedRoute>
                    <FeatureProtectedRoute featureFlag="enableReminders">
                      <PageWithTitle title="Reminders">
                        <RemindersPage />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'company/:company_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Company">
                      <CompanyPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'targets/',
                element: (
                  <ProtectedRoute>
                    <RedirectRestaurants />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'targets/:category/:sub_category/',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Targets">
                      <OpportunitiesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              // Redirect old opportunity URLs to targets (FIR-5979)
              {
                path: 'opportunities/',
                element: <Navigate to="/targets/" replace />,
              },
              {
                path: 'opportunities/:category/:sub_category/',
                element: <RedirectOpportunitiesToTargets />,
              },
              // TODO determine url paths
              {
                path: 'restaurant/:firstbite_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Restaurant">
                      <Restaurant />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'door/:door_proxy_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Door">
                      <DoorProxyPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'chain/:chain_name',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Chain">
                      <ChainPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'university/:university_name',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="University">
                      <University />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'k12/:id/',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="K12 District">
                      <K12DistrictPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'hospital/:hospital_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Hospital">
                      <HospitalPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'lodging/chain/:lodging_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Lodging & Hospitality">
                      <LodgingPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'lodging/door/:lodging_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Lodging & Hospitality">
                      <LodgingDoorPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'convenience/chain/:convenience_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Convenience Store">
                      <ConveniencePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'convenience/door/:convenience_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Convenience Store">
                      <ConvenienceDoorPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'nursinghome/:nursinghome_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Senior Living">
                      <NurseHomePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'products',
                element: (
                  <ProtectedRoute minimumRole="Limited">
                    <PageWithTitle title="Products">
                      <ProductsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'brands',
                element: (
                  <ProtectedRoute>
                    <FeatureProtectedRoute featureFlag="enableBrandManagement">
                      <PageWithTitle title="Brands">
                        <BrandsPage />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'settings',
                element: (
                  <ProtectedRoute minimumRole="Limited">
                    <PageWithTitle title="Settings">
                      <SettingsLayout />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
                children: [
                  {
                    index: true,
                    element: <SettingsIndexRedirect />,
                  },
                  {
                    path: 'company/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <CompanySettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'team/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <UsersSettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'email-integration/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <EmailIntegrationSettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'platform/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <PlatformSettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'data/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <DataSettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'notifications/:tab?',
                    element: <NotificationsSettingsPage />,
                  },
                  {
                    path: 'misc/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <MiscSettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'automations/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <AutomationsSettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'outreach/:tab?',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <OutreachSettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: 'email-outreach/:tab?',
                    element: (
                      <Navigate
                        to="/settings/outreach/email-outreach"
                        replace
                      />
                    ),
                  },
                  {
                    path: 'ai',
                    element: (
                      <ProtectedRoute minimumRole="Standard">
                        <AISettingsPage />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: 'deals',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Deals">
                      <DealsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'deals/:dealId',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Deal">
                      <DealDetailPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'campaigns',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Campaigns">
                      <CampaignsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'samples/:type?/',
                element: (
                  <ProtectedRoute minimumRole="Limited">
                    <FeatureProtectedRoute featureFlag="enableSamples">
                      <PageWithTitle title="Samples">
                        <SamplesDashboard />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'documents/:tab?',
                element: (
                  <ProtectedRoute>
                    <FeatureProtectedRoute featureFlag="enableDocumentLibrary">
                      <PageWithTitle title="Documents">
                        <DocLibraryPage />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'campaigns/:id',
                element: (
                  <ProtectedRoute>
                    <CampaignRouteLayout />
                  </ProtectedRoute>
                ),
                children: [
                  { index: true, element: <CampaignRouterPage /> },
                  { path: 'dashboard', element: <CampaignDashboardPage /> },
                  { path: 'detail', element: <CampaignDetailPage /> },
                  { path: 'sequence', element: <SequenceWizardPage /> },
                ],
              },
              {
                path: 'campaigns/:campaign_id/:tab?',
                element: (
                  <ProtectedRoute>
                    <CampaignRouterPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'rebates/:tab?',
                element: (
                  <ProtectedRoute>
                    <Rebates />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'rebates/offer/:offerId/:tab?',
                element: (
                  <ProtectedRoute>
                    <FeatureProtectedRoute featureFlag="enableRebates">
                      <PageWithTitle title="Rebate Offer">
                        <RebateOfferDetails />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'not-authorized',
                element: (
                  <ProtectedRoute minimumRole="Limited">
                    <NotAuthorized />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'component-library',
                element: (
                  <ProtectedRoute>
                    <FeatureProtectedRoute featureFlag="enableComponentPreview">
                      <PageWithTitle title="Component Library">
                        <ComponentLibraryPage />
                      </PageWithTitle>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reporting',
                element: (
                  <ProtectedRoute>
                    <ReportingPrototypeGate>
                      <PageWithTitle title="Reports">
                        <ReportingLibraryPage />
                      </PageWithTitle>
                    </ReportingPrototypeGate>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reporting/dashboards/new',
                element: (
                  <ProtectedRoute>
                    <ReportingPrototypeGate>
                      <PageWithTitle title="Build a dashboard">
                        <DashboardBuilderPage />
                      </PageWithTitle>
                    </ReportingPrototypeGate>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reporting/dashboards/:dashboardId/edit',
                element: (
                  <ProtectedRoute>
                    <ReportingPrototypeGate>
                      <PageWithTitle title="Edit dashboard">
                        <DashboardBuilderPage />
                      </PageWithTitle>
                    </ReportingPrototypeGate>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reporting/dashboards/:dashboardId',
                element: (
                  <ProtectedRoute>
                    <ReportingPrototypeGate>
                      <PageWithTitle title="Reports">
                        <ReportingDashboardPage />
                      </PageWithTitle>
                    </ReportingPrototypeGate>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reporting/new',
                element: (
                  <ProtectedRoute>
                    <ReportingPrototypeGate>
                      <PageWithTitle title="Reports">
                        <ReportBuilderPage />
                      </PageWithTitle>
                    </ReportingPrototypeGate>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reporting/:viewId',
                element: (
                  <ProtectedRoute>
                    <ReportingPrototypeGate>
                      <PageWithTitle title="Reports">
                        <ReportDetailPageWithKey />
                      </PageWithTitle>
                    </ReportingPrototypeGate>
                  </ProtectedRoute>
                ),
              },
              ...playgroundRoutes,
              {
                path: '*',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Page Not Found">
                      <NotFoundContent />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: '/login',
            element: <AuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Log-in">
                    <Login />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/signup',
            element: <AuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Sign-up">
                    <Register />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/sso-login',
            element: <AuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="SSO Login">
                    <SSOLogin />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/auth/sso-callback',
            element: (
              <PageWithTitle title="SSO Authentication">
                <SSOCallback />
              </PageWithTitle>
            ),
          },
          {
            path: '/email-verified-confirmation',
            element: <AuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Email Verified">
                    <Verified />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/email-verification-failed',
            element: <AuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Email Verification Failed">
                    <Failed />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/reset-password',
            element: <AuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Reset Password">
                    <ResetPassword />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/password-reset-confirm/:uid/:token',
            element: <AuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Reset Password Confirmation">
                    <ResetPasswordConfirm />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/terms-of-use',
            element: <CommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Terms of Use">
                    <Terms />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/privacy-policy',
            element: <CommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Privacy Policy">
                    <Privacy />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/subscription-agreement',
            element: <CommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Subscription Agreement">
                    <SubscriptionAgreement />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/rebate-platform-agreement',
            element: <CommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Rebate Platform Agreement">
                    <RebatePlatformAgreement />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/opt-out-form',
            element: <CommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Opt Out Form">
                    <OptOutForm />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/data-request-form',
            element: <CommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Data Request Form">
                    <DataRequestForm />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/data-request-success',
            element: <CommonLayout />,
            children: [{ path: '', element: <DataRequestSuccess /> }],
          },
          {
            path: '/opt-out-success',
            element: <CommonLayout />,
            children: [{ path: '', element: <OptOutSuccess /> }],
          },
          {
            path: PATHS.IMPORT_WIZARD,
            element: (
              <ProtectedRoute>
                <FeatureProtectedRoute featureFlag="enableNewImportTool">
                  <CommonLayout />
                </FeatureProtectedRoute>
              </ProtectedRoute>
            ),
            children: [
              { path: '', element: <ImportPreviewsPageV2 /> },
              { path: ':import_number', element: <ImportPreviewPageV2 /> },
            ],
          },
        ],
      },
    ],
    { basename: import.meta.env.BASE_URL }
  )

  return <RouterProvider router={appRouter} />
}
